import { memo } from "react";
import { useDraggable } from "@dnd-kit/core";
import { useNavigate } from "react-router-dom";
import type { OrderListItem } from "@school-sales-ai/shared";
import { OrderCardBody } from "./OrderCardBody";

type Props = {
  order: OrderListItem;
  isDragging: boolean;
};

/** A draggable order on the desktop board. A click opens the order. */
function OrderKanbanCardImpl({ order, isDragging }: Props): React.JSX.Element {
  const navigate = useNavigate();
  // The order rides along on `data`, so a drop can be resolved even for a card
  // that came from a column's "Ver más" page rather than the board's first one.
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: order.id,
    data: { order },
  });

  // dnd-kit hands the drag offset over as numbers; there is no class for it —
  // the same exception `KanbanCard` takes.
  const style = transform
    ? { transform: `translate(${transform.x}px, ${transform.y}px)` }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => navigate(`/pedidos/${order.id}`)}
      className={[
        "flex flex-col gap-1.5 rounded-lg border border-ink-100 bg-white p-3 shadow-sm",
        "cursor-grab touch-none active:cursor-grabbing",
        isDragging ? "opacity-40" : "hover:border-ink-200 hover:shadow-md",
      ].join(" ")}
    >
      <OrderCardBody order={order} />
    </div>
  );
}

// Memoized like `KanbanCard`: a drag re-renders the board on every pointer move.
export const OrderKanbanCard = memo(OrderKanbanCardImpl);
